import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/ui-store'
import { useAuthStore } from '@/stores/auth-store'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useHasPermission } from '@/hooks/useHasPermission'
import { Permissions } from '@/lib/permissions'
import { homeWorkspace, useWorkspaceAccess } from '@/features/auth/workspaceAccess'
import { Sidebar, SidebarContent } from '@/components/layout/Sidebar'
import { Header } from '@/components/layout/Header'
import { PageTransition } from '@/components/common/PageTransition'
import { CommandPalette } from '@/components/command-palette'
import { IdleScreensaver } from '@/components/common/IdleScreensaver'
import { PermissionDeniedToast } from '@/components/auth/PermissionDeniedToast'
import { SessionExpiredModal } from '@/components/auth/SessionExpiredModal'
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet'
import { FinanceSidebar, FinanceSidebarContent } from '@/features/finance/components/FinanceSidebar'
import { shouldHideSidebar } from '@/features/finance/utils/zones'

/**
 * Main application layout with sidebar and header
 * Per App-Shell.md Section 1 - Layout Overview
 *
 * Layout Structure:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  HEADER (h-14, fixed top)                                       │
 * ├──────────────┬──────────────────────────────────────────────────┤
 * │              │                                                  │
 * │   SIDEBAR    │              MAIN CONTENT                        │
 * │   (w-64,     │              (flex-1, overflow-auto)             │
 * │   fixed      │                                                  │
 * │   left)      │                                                  │
 * │              │                                                  │
 * │   Collapsed: │                                                  │
 * │   w-[72px]   │                                                  │
 * │              │                                                  │
 * └──────────────┴──────────────────────────────────────────────────┘
 *
 * Responsive Behavior (per App-Shell.md Section 2.5):
 * - Desktop (≥1024px): Sidebar visible, can be collapsed
 * - Tablet (768-1023px): Sidebar collapsed by default
 * - Mobile (<768px): Sidebar hidden, hamburger menu opens overlay
 */
export function AppLayout() {
  const isDesktop = useIsDesktop()
  const location = useLocation()
  const {
    setSidebarCollapsed,
    sidebarMobileOpen,
    setSidebarMobileOpen,
    workspace,
    setWorkspace,
  } = useUIStore()
  const hasFinanceAccess = useHasPermission(Permissions.FINANCE_ACCESS)
  const hasSalesAccess = useHasPermission(Permissions.SALES_ACCESS)
  // Not a Sales seat — the approver's door into /sales/onboarding. See the sync effect below.
  const canSeeOnboarding = useHasPermission(Permissions.ONBOARDING_CASE_VIEW)
  const access = useWorkspaceAccess()
  const refreshPermissions = useAuthStore((s) => s.refreshPermissions)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Check if we should hide sidebar (Finance start page)
  const isFinanceModule = location.pathname.startsWith('/finance')
  const hideSidebar = isFinanceModule && shouldHideSidebar(location.pathname)

  /**
   * Full-bleed pages: no document padding.
   *
   * The `p-4 lg:p-6` below is right for a page of content — a form, a list, a report. It is wrong
   * for a WORKSPACE that draws its own frame edge to edge. The schedule is a calendar grid with
   * its own header rows and borders, so the layout's 24 px was a band of nothing above the header
   * and another below the grid, on the one screen where vertical space is the scarce resource.
   *
   * Decided here, where the padding is, rather than undone by the page with negative margins — a
   * page cancelling its parent's padding has to hard-code that parent's value and silently breaks
   * when it changes.
   */
  const isFullBleed = /(^|\/)schedule$/.test(location.pathname)

  // Sync workspace with current route. When the user lacks the module permission for the URL's
  // workspace, fall back to their own home workspace (`homeWorkspace`), so the sidebar they see
  // is one they may use, beside the UnauthorizedModule shown by the route guard.
  //
  // `canSeeOnboarding` is the exception, and it is deliberate: `/sales/onboarding` is gated on
  // `onboarding-case:view`, which the four approver roles hold without any Sales seat. Without
  // this clause an approver opening their own queue was thrown into the OPERATIONS sidebar —
  // the workspace switcher said Operations, the URL said /sales, and the entry they had just
  // clicked was nowhere on the page. See `salesItems` in `Sidebar.tsx`, which filters that
  // workspace down to what they can actually open.
  useEffect(() => {
    const shouldBeFinance = location.pathname.startsWith('/finance')
    const shouldBeSales = location.pathname.startsWith('/sales')
    // Anywhere else, the person's own home workspace — Operations only if they may enter it
    // (workspaceAccess). A Sales-only user on a shared page keeps the Sales sidebar.
    let target: 'finance' | 'sales' | 'operations' = homeWorkspace(access)
    if (shouldBeFinance && hasFinanceAccess) target = 'finance'
    else if (shouldBeSales && (hasSalesAccess || canSeeOnboarding)) target = 'sales'
    if (workspace !== target) {
      setWorkspace(target)
    }
  }, [location.pathname, workspace, setWorkspace, hasFinanceAccess, hasSalesAccess, canSeeOnboarding, access])

  // RBAC v1 — refresh permissions on window focus so admin-driven permission
  // changes propagate without re-login. Per spec #10 §5.5.
  useEffect(() => {
    if (!isAuthenticated) return
    const onFocus = () => {
      refreshPermissions()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [isAuthenticated, refreshPermissions])

  // Auto-collapse sidebar on tablet, expand on desktop
  useEffect(() => {
    // Only auto-adjust on first load or when crossing breakpoint
    if (!isDesktop) {
      // On tablet/mobile, collapse sidebar
      setSidebarCollapsed(true)
    }
    // Note: We don't auto-expand on desktop to preserve user preference
  }, [isDesktop, setSidebarCollapsed])

  // Close mobile sidebar when navigating
  const handleMobileNavigation = () => {
    setSidebarMobileOpen(false)
  }

  // Determine which sidebar to show
  const showFinanceSidebar = workspace === 'finance' && isFinanceModule && !hideSidebar

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop/Tablet Sidebar - hidden on mobile and on Finance start page */}
      {!hideSidebar && (
        <div className="hidden lg:block">
          {showFinanceSidebar ? (
            <FinanceSidebar className="h-screen" />
          ) : (
            <Sidebar className="h-screen" />
          )}
        </div>
      )}

      {/* Mobile Sidebar - Sheet overlay */}
      <Sheet open={sidebarMobileOpen} onOpenChange={setSidebarMobileOpen}>
        <SheetContent side="left" className="w-72 p-0">
          {showFinanceSidebar ? (
            <FinanceSidebarContent onNavigate={handleMobileNavigation} />
          ) : (
            <SidebarContent onNavigate={handleMobileNavigation} />
          )}
        </SheetContent>
      </Sheet>

      {/* Main Content Area */}
      <div
        className={cn(
          'flex flex-1 flex-col overflow-hidden',
          'transition-all duration-200'
        )}
      >
        {/* Header — hidden when printing */}
        <div data-print-hide>
          <Header
            showMenuButton={!isDesktop}
            onMenuClick={() => setSidebarMobileOpen(true)}
          />
        </div>

        {/* Page Content - scrollable */}
        <main className="flex-1 overflow-auto">
          {/*
            `h-full` down to the page, so a page CAN own its own scrolling.

            Without it this wrapper and `PageTransition` were both auto-height, so a page asking
            for `h-full min-h-0` resolved against `auto` and grew to its content instead. Its own
            `overflow-y-auto` panes then never became scrollers and `<main>` scrolled the lot —
            which on the schedule meant the unscheduled rail and the day headers scrolled away
            with the grid, when the whole point of both is that they stay put.

            Pages taller than the viewport are unaffected: their content simply overflows this
            box and `<main>` scrolls as before. Padding is inside the height (border-box), so
            nothing gains a second scrollbar.
          */}
          <div className={cn('mx-auto h-full max-w-screen-2xl', !isFullBleed && 'p-4 lg:p-6')}>
            <PageTransition className="h-full">
              <Outlet />
            </PageTransition>
          </div>
        </main>
      </div>

      {/* Command Palette - Global, accessible via Cmd+K */}
      <CommandPalette />

      {/* Idle Screensaver — fades in after 5 min of inactivity */}
      <IdleScreensaver />

      {/* In-place re-auth when the access token expires mid-session (silent
          refresh failed). Overlays the current page without unmounting it, so
          in-progress work survives. */}
      <SessionExpiredModal />

      {/* RBAC v1 — listens to ui-store.permissionDenial and raises sonner toasts */}
      <PermissionDeniedToast />
    </div>
  )
}
